import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  RealtimeKitProvider,
  useRealtimeKitClient,
  useRealtimeKitMeeting,
} from "@cloudflare/realtimekit-react";
import { RtkMeeting } from "@cloudflare/realtimekit-react-ui";
import { Button } from "@/components/ui/button";
import { meetingRequiresAuthentication } from "@/lib/productHasPrice";
import { fetchLeadSession, logoutLeadSession } from "./leadSessionClient";

interface MeetingProduct {
  _id: string;
  name?: string;
  price?: number;
  variants?: { price?: number }[];
  requireAuthentication?: boolean;
  realtimeKit?: {
    meetingId?: string;
    appId?: string;
    status?: string;
  };
}

function MeetingUi({
  showLogout,
  loggingOut,
  onLogout,
}: {
  showLogout: boolean;
  loggingOut: boolean;
  onLogout: () => void;
}) {
  const { meeting } = useRealtimeKitMeeting();

  if (!meeting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        Connecting...
      </div>
    );
  }

  return (
    <div className="relative h-screen w-screen bg-black">
      {showLogout ? (
        <div className="absolute top-[max(1rem,env(safe-area-inset-top))] right-[max(1rem,env(safe-area-inset-right))] z-50">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={loggingOut}
            onClick={onLogout}
            className="border-white/20 bg-black/60 text-white hover:bg-white/10 hover:text-white"
          >
            {loggingOut ? "Logging out…" : "Log out"}
          </Button>
        </div>
      ) : null}
      <RtkMeeting meeting={meeting} mode="fill" showSetupScreen />
    </div>
  );
}

function MeetingExperience({
  authToken,
  showLogout,
  loggingOut,
  onLogout,
}: {
  authToken: string;
  showLogout: boolean;
  loggingOut: boolean;
  onLogout: () => void;
}) {
  const [meetingClient, initMeeting] = useRealtimeKitClient();

  useEffect(() => {
    initMeeting({ authToken });
  }, [authToken, initMeeting]);

  return (
    <RealtimeKitProvider value={meetingClient}>
      <MeetingUi showLogout={showLogout} loggingOut={loggingOut} onLogout={onLogout} />
    </RealtimeKitProvider>
  );
}

export function MeetingRoute() {
  const { id: productId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authTokenFromQuery = searchParams.get("authToken");
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [productName, setProductName] = useState("");
  const [meetingId, setMeetingId] = useState<string | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [requireAuthentication, setRequireAuthentication] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    if (!productId) return;

    const load = async () => {
      setLoading(true);
      setNotFound(false);
      setError(null);
      setAuthToken(null);
      setMeetingId(null);
      setNeedsAuth(false);
      setRequireAuthentication(false);

      try {
        const productRes = await fetch(`/api/products/${productId}`);
        if (!productRes.ok) {
          if (productRes.status === 404) setNotFound(true);
          else setError("Failed to load meeting");
          return;
        }

        const productData = (await productRes.json()) as { product?: MeetingProduct };
        const product = productData.product;

        if (!product?.realtimeKit?.meetingId) {
          setError("Meeting is not configured for this product");
          return;
        }

        setProductName(product.name?.trim() ?? "");
        setMeetingId(product.realtimeKit.meetingId);

        const requiresAuth = meetingRequiresAuthentication(product);
        setRequireAuthentication(requiresAuth);

        if (authTokenFromQuery) {
          setAuthToken(authTokenFromQuery);
          return;
        }

        let participantName: string | undefined;

        if (requiresAuth) {
          const authRes = await fetchLeadSession({ productId });
          const authData = (await authRes.json()) as {
            authenticated?: boolean;
            name?: string;
          };
          if (!authData.authenticated) {
            setNeedsAuth(true);
            return;
          }
          participantName = authData.name?.trim() || undefined;
        }

        const joinRes = await fetch(`/api/products/${productId}/meeting/join`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify(participantName ? { name: participantName } : {}),
        });

        const joinData = (await joinRes.json()) as { authToken?: string; error?: string };

        if (!joinRes.ok || !joinData.authToken) {
          if (joinRes.status === 401) {
            setNeedsAuth(true);
            return;
          }
          setError(joinData.error ?? "Failed to join meeting");
          return;
        }

        setAuthToken(joinData.authToken);
      } catch (e) {
        console.error("Meeting load error:", e);
        setError("Failed to load meeting");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [productId, authTokenFromQuery]);

  async function handleLogout() {
    if (!productId || loggingOut) return;

    setLoggingOut(true);
    try {
      await logoutLeadSession();
      navigate(`/meetings/${productId}/login`, { replace: true });
    } catch (e) {
      console.error("Meeting logout error:", e);
    } finally {
      setLoggingOut(false);
    }
  }

  const pageTitle = productName.trim() ? `${productName.trim()} · Meeting` : "Meeting";

  if (!productId) {
    return (
      <>
        <Helmet>
          <title>Meeting</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-black text-white">
          Missing meeting ID
        </div>
      </>
    );
  }

  if (needsAuth) {
    return <Navigate to={`/meetings/${productId}/login`} replace />;
  }

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-black text-white">
          Loading...
        </div>
      </>
    );
  }

  if (notFound) {
    return (
      <>
        <Helmet>
          <title>Meeting not found</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-black text-white">
          Meeting not found
        </div>
      </>
    );
  }

  if (error || !authToken || !meetingId) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-black text-white px-4 text-center">
          {error ?? "Unable to start meeting"}
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <MeetingExperience
        authToken={authToken}
        showLogout={requireAuthentication && !authTokenFromQuery}
        loggingOut={loggingOut}
        onLogout={handleLogout}
      />
    </>
  );
}
